/** Validate a Move action (rule.md §6). */
import { areAdjacent, isCell } from "../data/board";
import type { MoveAction } from "../types/action";
import { invalid, type ValidationResult, valid } from "../types/common";
import type { AugmentedWarState } from "../types/game-state";
import { inHomeZone, isEngaged } from "../utils/state";
import { validateActionBudget, validateTurn } from "./shared";

export function validateMove(
  state: AugmentedWarState,
  action: MoveAction,
): ValidationResult {
  const turn = validateTurn(state, action.playerId);
  if (!turn.valid) return turn;
  const budget = validateActionBudget(state, "move");
  if (!budget.valid) return budget;

  const { from, to, playerId } = action;
  if (state.board[from]?.owner !== playerId) {
    return invalid("No own unit at the source cell", { from });
  }
  if (!isCell(to)) {
    return invalid("Destination is not a board cell", { to });
  }
  if (state.board[to]) {
    return invalid("Destination cell is occupied", { to });
  }
  if (!areAdjacent(from, to)) {
    return invalid("Move must be a single step to an adjacent cell", {
      from,
      to,
    });
  }
  // Zoned Zone of Control: an engaged unit is pinned only inside a home zone
  // (must Attack or hold); the neutral midfield is a free race (rule.md §6).
  if (inHomeZone(from) && isEngaged(state.board, from, playerId)) {
    return invalid("Engaged unit in a home zone cannot Move — Attack or hold", {
      from,
    });
  }
  return valid;
}
